import { api } from './api';
import type {
  WishlistItem,
  WishlistResponse,
  AllWishlistsResponse,
  MyReservedItemsResponse,
  CreateWishlistItemDto,
  UpdateWishlistItemDto,
  WishlistFilters,
} from '@/types/gift.types';

const BASE_URL = '/wishlists';

export const wishlistService = {
  /**
   * Get my wishlist
   */
  async getMyWishlist(filters?: WishlistFilters): Promise<WishlistResponse> {
    const response = await api.get<{ data: WishlistResponse }>(`${BASE_URL}/me`, {
      params: filters,
    });
    return response.data.data;
  },

  /**
   * Get a user's wishlist by userId
   */
  async getUserWishlist(userId: string, filters?: WishlistFilters): Promise<WishlistResponse> {
    const response = await api.get<{ data: WishlistResponse }>(`${BASE_URL}/${userId}`, {
      params: filters,
    });
    return response.data.data;
  },

  /**
   * Get all wishlists (browse)
   */
  async getAllWishlists(): Promise<AllWishlistsResponse> {
    const response = await api.get<{ data: AllWishlistsResponse }>(`${BASE_URL}`);
    return response.data.data;
  },

  /**
   * Get a single wishlist item
   */
  async getWishlistItem(itemId: string): Promise<WishlistItem> {
    const response = await api.get<{ data: WishlistItem }>(`${BASE_URL}/${itemId}`);
    return response.data.data;
  },

  /**
   * Create a new wishlist item
   */
  async createWishlistItem(data: CreateWishlistItemDto): Promise<WishlistItem> {
    const response = await api.post<{ data: WishlistItem }>(`${BASE_URL}`, data);
    return response.data.data;
  },

  /**
   * Update a wishlist item
   */
  async updateWishlistItem(itemId: string, data: UpdateWishlistItemDto): Promise<WishlistItem> {
    const response = await api.patch<{ data: WishlistItem }>(`${BASE_URL}/${itemId}`, data);
    return response.data.data;
  },

  /**
   * Delete a wishlist item
   */
  async deleteWishlistItem(itemId: string): Promise<void> {
    await api.delete(`${BASE_URL}/${itemId}`);
  },

  /**
   * Reserve a wishlist item (toggle)
   */
  async reserveItem(itemId: string): Promise<WishlistItem> {
    const response = await api.post<{ data: WishlistItem }>(`${BASE_URL}/${itemId}/reserve`);
    return response.data.data;
  },

  /**
   * Unreserve a wishlist item
   */
  async unreserveItem(itemId: string): Promise<WishlistItem> {
    const response = await api.delete<{ data: WishlistItem }>(`${BASE_URL}/${itemId}/reserve`);
    return response.data.data;
  },

  /**
   * Mark item as purchased
   */
  async markAsPurchased(itemId: string): Promise<WishlistItem> {
    const response = await api.post<{ data: WishlistItem }>(`${BASE_URL}/${itemId}/purchased`);
    return response.data.data;
  },
};

export default wishlistService;
